/**
 * Created By: Tyrus Malmstrom
 * Creation Date: 08/29/2023
 * Description:
 *   - Mixin to house specific functionality and logic for the Adobe PDF SDK.
 */

/* eslint-disable */
/* global AdobeDC, dataLayer */

import {
  loadScript,
} from '../../components/utilities/helpers/loadScript';

import axios from 'axios';

export default {
  data() {
    return {
      adobeClientId: null,
      adobeDCView: null,
      previewPDFInstance: null,
      adobeViewerSDKScriptInjected: false,
      defaultPdfResourceIdentifier: 'PDF_Name.pdf',
      adobeClientIds: {
        'localhost': process.env.VUE_APP_ADOBE_LOCALHOST_ID,
        'api.playposit.com': process.env.VUE_APP_ADOBE_CLIENT_ID_PROD,
        'api-dev.playposit.com': process.env.VUE_APP_ADOBE_CLIENT_ID_DEV,
        'api-stage.playposit.com': process.env.VUE_APP_ADOBE_CLIENT_ID_STAGE,
        'app.playpos.it': process.env.VUE_APP_ADOBE_CLIENT_ID_APP,
      },
      eventOptions: {
        // This is required per Adobe's documentation for event system to work
        enableFilePreviewEvents: true,
        enablePDFAnalytics: true,
      },
    };
  },
  computed: {
    bearerToken() {
      return (
        this?.token
        ?? this?.urlToken
        ?? this?.instructorData?.token
        ?? this?.user?.token
      );
    },
    api() {
      return {
        init: () => {
          axios.defaults.headers.common.Authorization = `Bearer ${this.bearerToken}`;
          axios.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';
          axios.defaults.headers.common.Accept = 'application/json';
        },
        getAttachmentName: (attachmentResourceUrl) => axios.get(`${this.$getServerUrl()}attachment/name/${encodeURIComponent(attachmentResourceUrl)}`),
      };
    },
  },
  methods: {
    injectScript() {
      const adobeDCGlobalInstanceInterval = setInterval( () => {
        if (window.AdobeDC) {
          this.adobeViewerSDKScriptInjected = true;
          clearInterval(adobeDCGlobalInstanceInterval);
          this.constructSDKEventOptions();
          this.setAdobeId();
        }
      });
    },
    constructSDKEventOptions() {
      // Array of events to listen on - currently only listening to page chages.
      // Default ones we *always* listen for.
      this.eventOptions.listenOn = [
        AdobeDC.View.Enum.FilePreviewEvents.CURRENT_ACTIVE_PAGE,
        AdobeDC.View.Enum.PDFAnalyticsEvents.DOCUMENT_DOWNLOAD,
      ];
    },
    setAdobeId() {
      this.adobeClientId = this.adobeClientIds[window.location.hostname];
    },
    createAdobeViewerInstance({
      divId = 'pdf-view',
    } = {}) {
      // Set up the Adobe API:
      this.adobeDCView = new AdobeDC.View({
        clientId: this.adobeClientId,
        divId,
      });
    },
    /**
     * Generates the PDF view and embeds it in the div set above.
     * 
     * @param {
     *   params...
     * } 
     */
    async previewFile({
      resourceLocationUrl,
      resourceIdentifier,
      showDownloadPDF = true,
    } = {}) {
      const fileName = await this.determinePdfFileName(resourceIdentifier, resourceLocationUrl);
      this.previewPDFInstance = this.adobeDCView.previewFile({
        content: {
          location: {
            url: resourceLocationUrl,
          },
        },
        metaData: {
          fileName,
        },
      },
      {
        // Sets focus on the PDF when it loads:
        focusOnRendering: true,
        // Hides annotation tools in PDF view:
        showAnnotationTools: true,
        // Allow download
        showDownloadPDF,
      });
    },
    fetchPdfMetadata() {
      try {
        // Gets the PDF meta data - including total number of pages:
        return new Promise((resolve, reject) => {
          this.previewPDFInstance.then((adobeViewer) => {
            adobeViewer.getAPIs().then( (apis) => {
              apis.getPDFMetadata()
              .then((result) => {
                resolve(parseInt(result.numPages, 10));
              })
              .catch((error) => {
                reject(error);
              });
            });
          })
          .catch((error) => {
            if (EventBus) {
              EventBus.$emit(
                'triggerSnackbar',
                {
                  text: (
                    error?.message
                    || `Unable to process PDF file. This can sometimes be due to CORs issues, or the site not allowing the user to embed directly. Please download the resource and try again.`
                  ),
                  color: 'warning',
                  timeout: 10000,
                }
              );
            }
          });
        });
      } catch (error) {
        // pikachu, I choose you!
      }
    },
    initializePdfViewerEventListeners({
      vm,
      eventTypes = [],
    } = {}) {
      // Set up for the Adobe PDF events/callbacks:
      if (eventTypes.length) this.eventOptions.listenOn.concat(eventTypes);
      this.adobeDCView.registerCallback(
        AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
        async (event) => {
          vm.activePDFPage = parseInt(event?.data?.pageNumber, 10);
          if (['DOCUMENT_DOWNLOAD'].includes(event?.type)) {
            const { data: pdfData } = event;
            const documentDownloadEvent = `
              Playlist: pdf attachment downloaded --
              schoolId: ${this.user.schoolId}, playlistId: ${this.playlist.id}, fileName: ${pdfData.fileName}
            `;
            dataLayer.manualLog(documentDownloadEvent, 'playlistPdfAttachmentDownload');
          }
        },
        this.eventOptions,
      );
    },
    destroyAdobeViewer() {
      this.adobeDCView = null;
      this.previewPDFInstance = null;
      this.adobeViewerSDKScriptInjected = false;
    },
    /**
     * @param {String} resourceIdentifier 
     * @param {String} resourceLocationUrl 
     * @returns 
     */
    async determinePdfFileName(resourceIdentifier, resourceLocationUrl) {
      let finalFileName = '';
      if (resourceIdentifier) {
        finalFileName = resourceIdentifier;
      } else {
        const fetchedFileName = await this.fetchPdfResourceName(resourceLocationUrl);
        if (!fetchedFileName) finalFileName = this.defaultPdfResourceIdentifier
        else {
          const {
            data: {
              name,
            }
          } = fetchedFileName;
          finalFileName = name;
        }
      }

      return finalFileName;
    },
    async fetchPdfResourceName(attachmentResourceUrl) {
      try {
        return await this.api.getAttachmentName(attachmentResourceUrl);
      } catch (error) {
        console.error('Error fetching attachment name: ', error);
      }
    },
  },
  beforeDestroy() {
    if (this?.adobeDCView) this.destroyAdobeViewer();
  },
  created () {
    this.api.init();
  },
  mounted() {
    loadScript('https://documentservices.adobe.com/view-sdk/viewer.js', this.injectScript);
  },
};
